/**
 * Извлекает текст из узла pdfmake (рекурсивно)
 * @param {any} textNode - узел с текстом (строка, массив строк, массив объектов)
 * @returns {string} - извлеченный текст
 */
function extractText(textNode) {
    if (!textNode) return '';
    
    if (typeof textNode === 'string') {
        return textNode;
    }
    
    if (Array.isArray(textNode)) {
        return textNode.map(item => {
            if (typeof item === 'string') {
                return item;
            }
            if (typeof item === 'object' && item !== null) {
                // Если это объект с text, рекурсивно извлекаем текст
                return extractText(item.text) || '';
            }
            return '';
        }).join('');
    }
    
    if (typeof textNode === 'object' && textNode !== null && textNode.text) {
        return extractText(textNode.text);
    }
    
    return '';
}

/**
 * Создает canvas с повернутым текстом с поддержкой переносов строк.
 * Блок текста центрируется до поворота — иначе глифы выходят за bitmap, pdfmake
 * масштабирует картинку неверно и она наезжает на контент выше таблицы.
 *
 * @returns {{ dataUrl: string, fit: [number, number] }}
 */
function createRotatedTextCanvas(text, fontSize = 9, fontFamily = 'Times New Roman', angleDegrees = -90, debug = false) {
    const empty = { dataUrl: '', fit: [0, 0] };

    if (typeof document === 'undefined' || typeof document.createElement === 'undefined') {
        console.warn('Canvas API not available, cannot rotate text');
        return empty;
    }

    try {
        let processedText = text.replace(/<br\s*\/?>/gi, '\n');
        const lines = processedText.split('\n').map(line => line.trim()).filter(line => line.length > 0);

        if (lines.length === 0) {
            return empty;
        }

        const PT_TO_PX = 96 / 72;
        const DPR = 2;
        const fontPx = fontSize * PT_TO_PX * DPR;
        const lineHeightPx = fontPx * 1.35;
        const padPx = Math.ceil(6 * DPR);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = `${fontPx}px ${fontFamily}`;

        let maxLineWidthPx = 0;
        lines.forEach((line) => {
            maxLineWidthPx = Math.max(maxLineWidthPx, ctx.measureText(line).width);
        });

        const blockHeightPx = lines.length * lineHeightPx;

        canvas.width = Math.ceil(blockHeightPx + 2 * padPx);
        canvas.height = Math.ceil(maxLineWidthPx + 2 * padPx);

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        if (debug) {
            ctx.strokeStyle = '#FF0000';
            ctx.lineWidth = 2;
            ctx.strokeRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#00FF00';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(centerX, 0);
            ctx.lineTo(centerX, canvas.height);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, centerY);
            ctx.lineTo(canvas.width, centerY);
            ctx.stroke();
        }

        ctx.font = `${fontPx}px ${fontFamily}`;
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.save();
        ctx.translate(centerX, centerY);
        const angleRadians = (angleDegrees * Math.PI) / 180;
        ctx.rotate(angleRadians);

        lines.forEach((line, index) => {
            const y = -blockHeightPx / 2 + (index + 0.5) * lineHeightPx;
            ctx.fillText(line, 0, y);
        });

        ctx.restore();

        const fitWidthPt = lines.length * fontSize * 1.35;
        const fitHeightPt = maxLineWidthPx / (PT_TO_PX * DPR);

        return {
            dataUrl: canvas.toDataURL('image/png'),
            fit: [Math.ceil(fitWidthPt + 2), Math.ceil(fitHeightPt + 2)]
        };
    } catch (error) {
        console.error('Error creating rotated text canvas:', error);
        return empty;
    }
}

const ROTATED_WRITING_MODES = new Set(['vertical-rl', 'vertical-lr', 'sideways-rl', 'sideways-lr']);

/**
 * @param {object} node
 * @returns {string} нормализованное значение writing-mode или ''
 */
function extractWritingModeFromNode(node) {
    if (!node || typeof node !== 'object') return '';

    let wm = node.writingMode || node['writing-mode'];
    if (wm) return String(wm).trim().toLowerCase();

    if (node.style && typeof node.style === 'string') {
        const m = node.style.match(/writing-mode\s*:\s*([^;]+)/i);
        if (m) return String(m[1]).trim().toLowerCase();
    }

    if (node.style && typeof node.style === 'object' && node.style !== null) {
        wm = node.style['writing-mode'] || node.style['writingMode'];
        if (wm) return String(wm).trim().toLowerCase();
    }

    return '';
}

/** Угол поворота canvas (центрированный блок); +90 для sideways-lr расходился с SunEditor */
function getCanvasRotationForWritingMode() {
    return -90;
}

/**
 * Проверяет, нужно ли для ячейки отрисовать вертикальный текст как повернутый canvas
 * @param {object} node - узел pdfmake
 * @returns {boolean}
 */
function hasVerticalWritingMode(node) {
    const wm = extractWritingModeFromNode(node);
    return ROTATED_WRITING_MODES.has(wm);
}

/** SunEditor / html-to-pdfmake: класс `vertical-text` в массиве style */
function hasVerticalTextClass(node) {
    if (!node || typeof node !== 'object') return false;
    if (Array.isArray(node.style) && node.style.some((s) => String(s) === 'vertical-text')) return true;
    return false;
}

function hasVerticalTableCellStyle(node) {
    return hasVerticalWritingMode(node) || hasVerticalTextClass(node);
}

/**
 * Вертикаль часто только на вложенном font; поворот дочернего узла даёт image внутри text[] —
 * pdfmake тогда ломает measureLeaf. Нужно поднимать замену на уровень ячейки.
 */
function cellNeedsVerticalCanvas(node) {
    if (!node || typeof node !== 'object') return false;
    if (node.image) return false;
    if (hasVerticalTableCellStyle(node)) return true;
    if (Array.isArray(node.text) && node.text.some(hasVerticalTableCellStyle)) return true;
    if (Array.isArray(node.stack) && node.stack.some(hasVerticalTableCellStyle)) return true;
    return false;
}

/**
 * Весь видимый текст ячейки (для одного canvas): stack → строки через \n, text[] — склейка как одна строка.
 */
function extractEntireCellText(node) {
    if (node == null || typeof node !== 'object') return '';
    if (node.image || node.canvas || node.svg || node.table) return '';

    if (node.stack && Array.isArray(node.stack)) {
        return node.stack
            .map((item) => extractEntireCellText(item))
            .map((s) => String(s).trim())
            .filter((s) => s.length > 0)
            .join('\n');
    }

    if (node.text !== undefined) {
        const t = node.text;
        if (Array.isArray(t)) {
            return t
                .map((item) => {
                    if (typeof item === 'string') return item;
                    if (item && typeof item === 'object') return extractEntireCellText(item);
                    return '';
                })
                .join('');
        }
        if (typeof t === 'object' && t !== null) return extractEntireCellText(t);
        return extractText(t);
    }

    return '';
}

function replaceCellWithRotatedCanvas(node, textStr, debug) {
    const fontSize = 9;
    const cellAngle = getCanvasRotationForWritingMode();
    const rotated = createRotatedTextCanvas(textStr, fontSize, 'Times New Roman', cellAngle, debug);

    if (!rotated.dataUrl) return false;

    const cellProperties = {
        alignment: node.alignment,
        verticalAlignment: node.verticalAlignment,
        margin: node.margin,
        fillColor: node.fillColor,
        border: node.border,
        colSpan: node.colSpan,
        rowSpan: node.rowSpan
    };

    delete node.text;
    delete node.stack;
    delete node.width;
    delete node.height;

    node.image = rotated.dataUrl;
    node.fit = rotated.fit;
    node.alignment = 'center';
    node.verticalAlignment = 'middle';

    if (cellProperties.colSpan !== undefined) node.colSpan = cellProperties.colSpan;
    if (cellProperties.rowSpan !== undefined) node.rowSpan = cellProperties.rowSpan;
    if (cellProperties.fillColor !== undefined) node.fillColor = cellProperties.fillColor;
    if (cellProperties.border !== undefined) node.border = cellProperties.border;
    node.margin = [0, 0, 0, 0];

    if (node.writingMode !== undefined) delete node.writingMode;
    if (node['writing-mode'] !== undefined) delete node['writing-mode'];
    if (typeof node.style === 'string') {
        let s = node.style
            .replace(/writing-mode\s*:\s*[^;]+;?/gi, '')
            .replace(/text-orientation\s*:\s*[^;]+;?/gi, '')
            .replace(/mso-rotate\s*:\s*[^;]+;?/gi, '')
            .replace(/;\s*;/g, ';')
            .replace(/^\s*;\s*|\s*;\s*$/g, '')
            .trim();
        if (s) node.style = s;
        else delete node.style;
    }
    if (typeof node.style === 'object' && node.style !== null && !Array.isArray(node.style)) {
        delete node.style['writing-mode'];
        delete node.style['writingMode'];
        delete node.style['text-orientation'];
        delete node.style['textOrientation'];
    }
    if (Array.isArray(node.style)) {
        const next = node.style.filter((s) => String(s) !== 'vertical-text');
        if (next.length) node.style = next;
        else delete node.style;
    }
    return true;
}

/**
 * Рекурсивно ищет и поворачивает текст в ячейках таблицы с writing-mode: vertical-rl
 * Использует canvas для создания повернутого текста как изображения
 * @param {any} node - узел pdfmake (таблица или ячейка)
 * @param {number} angleDegrees - угол поворота в градусах (по умолчанию -90)
 * @param {boolean} debug - включить отладочные линии в canvas (по умолчанию false)
 */
export function rotateTextInTable(node, angleDegrees = -90, debug = false) {
    if (!node) return;

    // Если это таблица, обрабатываем её body
    if (node.table && node.table.body) {
        node.table.body.forEach(row => {
            if (Array.isArray(row)) {
                row.forEach(cell => {
                    rotateTextInTable(cell, angleDegrees, debug);
                });
            } else {
                rotateTextInTable(row, angleDegrees, debug);
            }
        });
        return;
    }

    // Если это ячейка (объект с text или stack)
    if (typeof node === 'object') {
        if (cellNeedsVerticalCanvas(node)) {
            const textStr = extractEntireCellText(node);
            if (textStr && replaceCellWithRotatedCanvas(node, textStr, debug)) {
                return;
            }
        }

        if (Array.isArray(node.text)) {
            node.text.forEach((item) => {
                if (typeof item === 'object' && item !== null) {
                    rotateTextInTable(item, angleDegrees, debug);
                }
            });
        }

        if (node.stack && Array.isArray(node.stack)) {
            node.stack.forEach((item) => {
                rotateTextInTable(item, angleDegrees, debug);
            });
        }

        for (const key in node) {
            if (key !== 'text' && key !== 'stack' && key !== 'image' && typeof node[key] === 'object' && node[key] !== null) {
                rotateTextInTable(node[key], angleDegrees, debug);
            }
        }
    }
}